import { useEffect, useRef, useState } from "react";
import { api, cachedGet } from "@/lib/apiClient";
import { Clock, AlertTriangle, CheckCircle2 } from "lucide-react";

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("pt-PT", { weekday: "long", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function fmtCountdown(secs) {
  if (secs <= 0) return "expirado";
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function DeadlineBanner({ compact = false }) {
  const [s, setS] = useState(null);
  const [now, setNow] = useState(Date.now());
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    cachedGet("/settings").then((r) => { if (aliveRef.current) setS(r.data); }).catch(() => {});
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => { aliveRef.current = false; clearInterval(t); };
  }, []);

  if (!s) return null;
  // Hide entirely when edition has not started yet — no point showing "starts in X days"
  // while admin is still testing and configuring.
  if (s.status === "upcoming") return null;
  const deadlineMs = new Date(s.week_deadline_iso).getTime();
  const isEnded = s.status === "ended";
  const secsLeft = Math.max(0, Math.floor((deadlineMs - now) / 1000));
  const past = !isEnded && secsLeft === 0;

  const Icon = past ? AlertTriangle : isEnded ? CheckCircle2 : Clock;
  const color = past ? "#FF6B6B" : "#E50000";
  let title;
  if (isEnded) title = `Edição terminada · Semana ${s.weeks_total}/${s.weeks_total}`;
  else if (past) title = `Prazo da semana ${s.current_week_computed} já passou`;
  else title = `Faltam ${fmtCountdown(secsLeft)} para o fim da semana ${s.current_week_computed}`;
  const subtitle = `Deadline: ${fmtDateTime(s.week_deadline_iso)} · Após o prazo, o reporting fica "Não Reportou" (-10 pts).`;

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-xs" data-testid="deadline-compact">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="font-bold uppercase tracking-wider" style={{ color }}>{title}</span>
      </div>
    );
  }

  return (
    <div className="kw-card p-5 flex items-start gap-4" style={{ borderColor: past ? "#E50000" : "#222" }} data-testid="deadline-banner">
      <Icon className="w-6 h-6 mt-1 flex-shrink-0" style={{ color }} />
      <div className="flex-1">
        <div className="font-display text-xl font-black uppercase tracking-tight" style={{ color }}>
          {title}
        </div>
        <div className="text-sm text-[#A3A3A3] mt-1">{subtitle}</div>
      </div>
      {!isEnded && (
        <div className="text-right">
          <div className="font-display text-3xl font-black" style={{ color }}>{fmtCountdown(secsLeft)}</div>
          <div className="kw-overline">restantes</div>
        </div>
      )}
    </div>
  );
}
